import { JOB_CONTEXT } from '../job/jobContext';

/** Last closed-stretch bundle per customer. Survives tab switches like the opening store. */
const storedBundles = new Map();

function storeKey(tenantId, customerId) {
  return `${tenantId}:${String(customerId)}`;
}

export function getStoredClosedStretch(tenantId, customerId) {
  if (!tenantId || customerId == null) return null;
  return storedBundles.get(storeKey(tenantId, customerId)) || null;
}

export function storeClosedStretchBundles(tenantId, resultMap) {
  if (!tenantId || !resultMap || typeof resultMap.forEach !== 'function') return;
  resultMap.forEach((bundle, customerId) => {
    if (bundle) storedBundles.set(storeKey(tenantId, customerId), bundle);
  });
}

export function clearStoredClosedStretch() {
  storedBundles.clear();
}

export function invalidateStoredClosedStretchForCustomer(customerId) {
  if (customerId == null) return;
  const suffix = `:${String(customerId)}`;
  for (const key of storedBundles.keys()) {
    if (key.endsWith(suffix)) storedBundles.delete(key);
  }
}

function dateOnly(value) {
  const d = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

function sortJobs(jobs) {
  return [...jobs].sort((a, b) => {
    const byDate = String(a.date).localeCompare(String(b.date));
    if (byDate !== 0) return byDate;
    return String(a.id).localeCompare(String(b.id));
  });
}

function toVisit(job) {
  return {
    id: job.id,
    date: dateOnly(job.date),
    total: job.total,
    unpriced_line_count: job.unpriced_line_count,
    visit_reason: job.visit_reason || null,
  };
}

/**
 * Off-season visits during the current closed stretch. Unlike the opening /
 * closing bundle, billed jobs stay — this is "have we been here", not "what
 * still stages onto an invoice".
 *
 * @returns {Map<string, { jobCount: number, total: number, jobs: Array, windowStart: string|null, windowUnknown: boolean }>}
 */
export function assembleClosedStretchBundles({
  customerIds = [],
  windowStartByCustomer = new Map(),
  jobRows = [],
  transitionDate,
}) {
  const grouped = new Map(customerIds.map((id) => [String(id), []]));
  for (const job of jobRows || []) {
    const cid = String(job?.customer_id ?? '');
    if (!grouped.has(cid)) continue;
    const d = dateOnly(job.date);
    if (!d) continue;
    if (transitionDate && d > transitionDate) continue;
    const windowStart = windowStartByCustomer.get(cid);
    if (windowStart && d < windowStart) continue;
    grouped.get(cid).push(toVisit(job));
  }

  const out = new Map();
  for (const id of customerIds) {
    const cid = String(id);
    const jobs = sortJobs(grouped.get(cid) || []);
    const total = jobs.reduce((sum, job) => sum + (Number(job.total) || 0), 0);
    out.set(cid, {
      jobCount: jobs.length,
      total,
      jobs,
      windowStart: windowStartByCustomer.get(cid) ?? null,
      windowUnknown: false,
    });
  }
  return out;
}

/**
 * A visit logged after the fetch (still on the dashboard `jobs` array) joins
 * the stretch immediately. Jobs from before this closed window stay out.
 * Returns the same Map when nothing new qualifies.
 */
export function mergeDashboardOffSeasonJobs(bundles, jobs, transitionDate) {
  if (!bundles?.size || !jobs?.length) return bundles;
  let next = null;
  bundles.forEach((bundle, cid) => {
    if (!bundle?.windowStart || bundle.windowUnknown) return;
    const seen = new Set((bundle.jobs || []).map((job) => String(job.id)));
    const extras = [];
    for (const job of jobs) {
      if (String(job?.customer_id) !== cid) continue;
      if (job.job_context !== JOB_CONTEXT.OFF_SEASON) continue;
      if (seen.has(String(job.id))) continue;
      const d = dateOnly(job.date);
      if (!d || d < bundle.windowStart) continue;
      if (transitionDate && d > transitionDate) continue;
      extras.push(toVisit(job));
    }
    if (!extras.length) return;
    const merged = sortJobs([...(bundle.jobs || []), ...extras]);
    const total = merged.reduce((sum, job) => sum + (Number(job.total) || 0), 0);
    if (!next) next = new Map(bundles);
    next.set(cid, { ...bundle, jobs: merged, jobCount: merged.length, total });
  });
  return next || bundles;
}
